export type VenturuAiIntent = {
  key: string;
  label: string;
  description: string;
  surface: "campaigns" | "replies" | "pipeline";
};

export const VENTURU_AI_INTENTS: VenturuAiIntent[] = [
  {
    key: "campaign.new_listing_announcement",
    label: "When a new listing is ready",
    description: "Prepare an early look for the buyers most likely to care.",
    surface: "campaigns",
  },
  {
    key: "campaign.matched_buyer_opportunities",
    label: "When buyers match your listings",
    description: "Prepare a short list of businesses that fit their search.",
    surface: "campaigns",
  },
  {
    key: "campaign.buyer_nurture",
    label: "When a buyer may need a nudge",
    description: "Keep a buyer relationship warm without writing every note yourself.",
    surface: "campaigns",
  },
  {
    key: "campaign.seller_nurture",
    label: "When an owner may be ready",
    description: "Start a respectful, confidential sale conversation.",
    surface: "campaigns",
  },
  {
    key: "outreach.pipeline_seller_first_touch",
    label: "When you need to reach a new seller lead",
    description: "Prepare a personal first email using the lead and verified research.",
    surface: "pipeline",
  },
  {
    key: "outreach.pipeline_buyer_first_touch",
    label: "When you need to reach a new buyer lead",
    description: "Prepare a personal first email using the inquiry and verified research.",
    surface: "pipeline",
  },
  {
    key: "reminder.seller_documents_pending",
    label: "When seller documents are pending",
    description: "Remind sellers about the documents needed for their valuation.",
    surface: "pipeline",
  },
  {
    key: "reminder.intake_incomplete",
    label: "When an intake is incomplete",
    description: "Remind a client to finish an intake they started.",
    surface: "pipeline",
  },
  {
    key: "reminder.nda_unsigned",
    label: "When an NDA is waiting",
    description: "Remind a client to review and sign the NDA.",
    surface: "pipeline",
  },
  {
    key: "reminder.listing_agreement_unsigned",
    label: "When a listing agreement is waiting",
    description: "Remind a seller to review and sign the listing agreement.",
    surface: "pipeline",
  },
  {
    key: "reminder.portal_link_unopened",
    label: "When a client has not opened their portal",
    description: "Remind a client where to find their secure deal portal.",
    surface: "pipeline",
  },
  {
    key: "reminder.buyer_prequalification_pending",
    label: "When buyer prequalification is pending",
    description: "Remind a buyer to complete the next qualification step.",
    surface: "pipeline",
  },
  {
    key: "reminder.data_room_invite_unopened",
    label: "When data room access is unopened",
    description: "Remind a client to open the shared deal materials.",
    surface: "pipeline",
  },
  {
    key: "reply.inbound_acknowledgement",
    label: "When someone replies",
    description: "Prepare a thoughtful first response and show it to you.",
    surface: "replies",
  },
  {
    key: "reply.pipeline_seller_lead_welcome",
    label: "When an owner submits a form",
    description:
      "Only Automatic sends a welcome email. Ask me first leaves first outreach on Next steps until you draft it.",
    surface: "pipeline",
  },
  {
    key: "reply.pipeline_buyer_inquiry_ack",
    label: "When a buyer submits a form",
    description:
      "Only Automatic sends a welcome email. Ask me first leaves first outreach on Next steps until you draft it.",
    surface: "pipeline",
  },
  {
    key: "reply.pipeline_seller_follow_up",
    label: "When a seller has not replied",
    description: "Prepare the next respectful follow-up using the full contact history.",
    surface: "pipeline",
  },
  {
    key: "reply.pipeline_buyer_follow_up",
    label: "When a buyer has not replied",
    description: "Prepare the next buyer check-in without repeating the last message.",
    surface: "pipeline",
  },
  {
    key: "reply.acquisition_target_follow_up",
    label: "When an acquisition target has not replied",
    description: "Prepare a grounded target follow-up after the next attempt is due.",
    surface: "pipeline",
  },
];

export const FORM_INTAKE_REPLY_KEYS = [
  "reply.pipeline_seller_lead_welcome",
  "reply.pipeline_buyer_inquiry_ack",
] as const;

export function isFormIntakeReplyRecipe(recipeKey: string): boolean {
  return (FORM_INTAKE_REPLY_KEYS as readonly string[]).includes(recipeKey);
}

export function shouldAutoPrepareFormIntakeReply(input: {
  welcomeMode?: string | null;
  firstTouchMode?: string | null;
  archivedAt?: Date | null;
  deletedAt?: Date | null;
}): boolean {
  if (input.archivedAt || input.deletedAt) return false;
  return input.welcomeMode === "AUTO" || input.firstTouchMode === "AUTO";
}
